import React, { useState, useCallback, useMemo } from 'react';
import InvoiceItemListBootstrap from './InvoiceItemListBootstrap';
import { getValidationState } from '../utils/invoiceValidation';

const InvoiceFormBootstrap = ({
  formData,
  onFormDataChange,
  availableArticles,
  loading = false,
  invoiceSettings,
  validationErrors = {}
}) => {
  // Track which fields have been touched for validation display
  const [touchedFields, setTouchedFields] = useState({});

  // Mark field as touched
  const handleFieldTouch = useCallback((fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  }, []);

  // Handle items change
  const handleItemsChange = useCallback((items) => {

    console.log('Items changed:', items);

    handleFieldTouch('items');
    onFormDataChange({ items });
  }, [onFormDataChange, handleFieldTouch]);

  // Handle rounding change
  const handleRoundingChange = useCallback((roundValue) => {
    onFormDataChange({ roundTotal: roundValue });
  }, [onFormDataChange]);

  // Handle date change with validation
  const handleDateChange = useCallback((e, dateType) => {
    handleFieldTouch(dateType);
    try {
      const date = new Date(e.target.value);
      if (!isNaN(date.getTime())) {
        onFormDataChange({ [dateType]: date });
      }
    } catch (error) {
      console.error('Invalid date:', error);
    }
  }, [onFormDataChange, handleFieldTouch]);

  // Handle text field changes
  const handleFieldChange = useCallback((fieldName, value) => {
    handleFieldTouch(fieldName);
    onFormDataChange({ [fieldName]: value });
  }, [onFormDataChange, handleFieldTouch]);

  // Format date for input - use local date to avoid timezone offset issues
  const formatDate = useCallback((date) => {
    if (!date) return '';
    try {
      if (date instanceof Date && !isNaN(date)) {
        // Use local date components to avoid UTC conversion issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    } catch (e) {
      return '';
    }
  }, []);

  // Get validation props for form fields
  const getFieldValidation = useCallback((fieldName) => {
    return getValidationState(fieldName, validationErrors, touchedFields);
  }, [validationErrors, touchedFields]);

  // Currency options for the dropdown
  const currencyOptions = useMemo(() => [
    { value: 'SEK', label: 'SEK - Svenska kronor' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'NOK', label: 'NOK - Norska kronor' },
    { value: 'DKK', label: 'DKK - Danska kronor' }
  ], []);


  return (
    <>
      <style>{`
      .invoice-form-container {
        max-width: 100%;
        animation: fadeIn 0.3s ease-in;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .invoice-form-header {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 1.5rem;
        margin-bottom: 2rem;
      }
      
      .invoice-form-header-content {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      
      .invoice-form-icon {
        width: 48px;
        height: 48px;
        background: #0f172a;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.25rem;
        flex-shrink: 0;
      }
      
      .invoice-form-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
      }
      
      .invoice-form-subtitle {
        font-size: 0.875rem;
        color: #64748b;
        margin: 0.25rem 0 0 0;
        line-height: 1.4;
      }
      
      .form-section {
        background: white;
        border-radius: 12px;
        padding: 2rem;
        margin-bottom: 1.5rem;
        border: 1px solid #e5e7eb;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
      
      .section-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #f1f5f9;
      }
      
      .section-icon {
        width: 40px;
        height: 40px;
        background: #f0f9ff;
        border: 1px solid #0284c7;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0284c7;
        font-size: 1.25rem;
      }
      
      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
      }
      
      .section-subtitle {
        font-size: 0.875rem;
        color: #64748b;
        margin: 0.25rem 0 0 0;
      }
      
      .form-label-modern {
        display: block;
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
        margin-bottom: 0.5rem;
      }
      
      .required-asterisk {
        color: #dc2626;
        margin-left: 0.25rem;
      }
      
      .form-help {
        font-size: 0.75rem;
        color: #6b7280;
        margin-left: 0.5rem;
        font-weight: 400;
      }
      
      .form-control-modern {
        display: block;
        width: 100%;
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.5;
        color: #1f2937;
        background-color: #ffffff;
        background-clip: padding-box;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      }
      
      .form-control-modern:focus {
        color: #1f2937;
        background-color: #ffffff;
        border-color: var(--bs-primary);
        outline: 0;
        box-shadow: 0 0 0 3px rgba(22, 107, 72, 0.1);
      }
      
      .form-control-modern.is-invalid {
        border-color: #dc2626;
      }
      
      .form-control-modern.is-invalid:focus {
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
      }
      
      .form-select-modern {
        display: block;
        width: 100%;
        padding: 0.75rem 2.5rem 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.5;
        color: #1f2937;
        background-color: #ffffff;
        background-repeat: no-repeat;
        background-position: right 0.75rem center;
        background-size: 16px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        cursor: pointer;
      }
      
      .form-select-modern:focus {
        border-color: var(--bs-primary);
        outline: 0;
        box-shadow: 0 0 0 3px rgba(36, 126, 45, 0.1);
      }
      
      .form-select-modern.is-invalid {
        border-color: #dc2626;
      }
      
      .form-text-modern {
        margin-top: 0.5rem;
        font-size: 0.75rem;
        color: #6b7280;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .invalid-feedback-modern {
        width: 100%;
        margin-top: 0.5rem;
        font-size: 0.75rem;
        color: #dc2626;
        font-weight: 500;
      }
      
      .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1.5rem;
      }
      
      .form-grid-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
      }
      
      @media (max-width: 768px) {
        .invoice-form-header {
          padding: 1rem;
        }
        
        .invoice-form-header-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;
        }
        
        .form-section {
          padding: 1.5rem;
        }
        
        .form-grid-2 {
          grid-template-columns: 1fr;
        }
        
        .section-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }
      }
    `}</style>

      <div className="invoice-form-container">
        {/* Header Section */}
        <div className="invoice-form-header">
          <div className="invoice-form-header-content">
            <div className="invoice-form-icon">
              <i className="bi bi-file-earmark-text"></i>
            </div>
            <div>
              <h2 className="invoice-form-title">Fakturainformation</h2>
              <p className="invoice-form-subtitle">
                Fyll i grundläggande information om fakturan, lägg till produkter och tjänster som ska faktureras,
                och inkludera eventuella meddelanden till kunden. Alla obligatoriska fält måste fyllas i.
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Details Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-calendar-event"></i>
            </div>
            <div>
              <h2 className="section-title">Fakturadetaljer</h2>
              <p className="section-subtitle">Grundläggande information om fakturan</p>
            </div>
          </div>

          <div className="form-grid">
            {/* Invoice Number */}
            <div>
              <label className="form-label-modern">
                Fakturanummer
                <span className="form-help">(Automatisk numrering)</span>
              </label>
              <input
                type="text"
                className={`form-control-modern ${getFieldValidation('invoiceNumber').isInvalid ? 'is-invalid' : ''}`}
                value={formData.invoiceNumber || ''}
                onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
                placeholder="Genereras automatiskt"
                disabled={true}
                readOnly={true}
              />
              {getFieldValidation('invoiceNumber').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.invoiceNumber}
                </div>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="form-label-modern">
                Valuta
                <span className="required-asterisk">*</span>
              </label>
              <select
                className={`form-select-modern ${getFieldValidation('currency').isInvalid ? 'is-invalid' : ''}`}
                value={formData.currency || 'SEK'}
                onChange={(e) => handleFieldChange('currency', e.target.value)}
              >
                {currencyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {getFieldValidation('currency').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.currency}
                </div>
              )}
            </div>

            {/* Invoice Date */}
            <div>
              <label className="form-label-modern">
                Fakturadatum
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className={`form-control-modern ${getFieldValidation('invoiceDate').isInvalid ? 'is-invalid' : ''}`}
                value={formatDate(formData.invoiceDate)}
                onChange={(e) => handleDateChange(e, 'invoiceDate')}
                required
              />
              {getFieldValidation('invoiceDate').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.invoiceDate}
                </div>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="form-label-modern">
                Förfallodatum
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className={`form-control-modern ${getFieldValidation('dueDate').isInvalid ? 'is-invalid' : ''}`}
                value={formatDate(formData.dueDate)}
                onChange={(e) => handleDateChange(e, 'dueDate')}
                required
              />
              {getFieldValidation('dueDate').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.dueDate}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Items Section - Now consistent with other sections */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-list-ul"></i>
            </div>
            <div>
              <h2 className="section-title">Produkter & Tjänster</h2>
              <p className="section-subtitle">
                {formData.items?.length > 0
                  ? `${formData.items.length} artikel${formData.items.length !== 1 ? 'ar' : ''} tillagda`
                  : 'Lägg till artiklar som ska faktureras'
                }
              </p>
            </div>
          </div>

          <InvoiceItemListBootstrap
            items={formData.items || []}
            availableArticles={availableArticles}
            onItemsChange={handleItemsChange}
            roundTotal={formData.roundTotal || false}
            onRoundingChange={handleRoundingChange}
            defaultVatRate={formData.defaultVatRate || invoiceSettings?.defaultVatRate || 25}
            currency={formData.currency || 'SEK'}
          />
        </div>

        {/* Notes Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-chat-text"></i>
            </div>
            <div>
              <h2 className="section-title">Anteckningar</h2>
              <p className="section-subtitle">Lägg till meddelanden som visas på fakturan</p>
            </div>
          </div>

          <div>
            <label className="form-label-modern">
              Meddelande till kund
              <span className="form-help">(visas på fakturan)</span>
            </label>
            <textarea
              className={`form-control-modern ${getFieldValidation('notes').isInvalid ? 'is-invalid' : ''}`}
              rows="4"
              placeholder="Lägg till information, villkor eller andra meddelanden som ska visas på fakturan..."
              value={formData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              maxLength={2000}
            />
            <div className="form-text-modern">
              <span>Lägg till betalningsvillkor, leveransinformation eller andra viktiga meddelanden</span>
              <span>{formData.notes?.length || 0} / 2000 tecken</span>
            </div>
            {getFieldValidation('notes').isInvalid && (
              <div className="invalid-feedback-modern">
                {validationErrors.notes}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceFormBootstrap;
